import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getAuditLogsApi } from "../../../redux/features/EmployeeTimeCard";
import { RootState, useAppDispatch } from "../../../redux/store";
import { useSelector } from "react-redux";
import PullToRefresh from "pulltorefreshjs";
import {
  formatTimeCardDate,
  formatTimeCardTime,
} from "../../../utils/employeeTimeCardhelper";
import {
  setShowTopNav,
  setTopNavNameTitle,
} from "../../../redux/features/mobileLayout";
import { ArrowDownCircle, ArrowUpCircle, Loader, Pencil, PlusCircle, Trash2 } from "lucide-react";

interface ListTypes {
  type: "add" | "update" | "delete" | "clockin" | "clockout";
  timeRange?: string;
  duration?: string;
  comment?: string;
  user: string;
  timestamp: string;
}

const ICONS: Record<ListTypes["type"],  React.ReactNode> = {
  add: <PlusCircle />,
  update: <Pencil />,
  delete: <Trash2 />,
  clockin: <ArrowUpCircle />,
  clockout: <ArrowDownCircle />,
};

const ChangeHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const user = useSelector((state: any) => state?.user?.user);
  const [loading, setLoading] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const { getAuditLogsData } = useSelector(
    (state: RootState) => state.employeeTimeCardData
  );
  const valueMap = {
    breaktimes_json: "Breaktimes",
    record_code: "Code",
    department_code: "Department",
    column_name_updated: "OUT",
    os_emp_end_datetime: "Reg Hours",
    deductions: "Deductions",
    us_user_site_profile_id: "Site Profile",
    // Add more mappings as necessary
  } as const;

  type ValueMapKeys = keyof typeof valueMap;

  const displayColumnValue = (columnValue: ValueMapKeys): string => {
    return valueMap[columnValue] || " "; // Default message
  };

  useEffect(() => {
    if (user?.id) {
      setLoading(true);
      dispatch(
        getAuditLogsApi({ id: Number(id), employee_id: user?.id })
      ).finally(() => setLoading(false));
    }
  }, [dispatch, user?.id, id]);

  useEffect(() => {
    dispatch(
      setTopNavNameTitle(`Audit History(${Array.isArray(getAuditLogsData) ? getAuditLogsData?.length : 0} logs)`)
    );
  }, [getAuditLogsData, dispatch]);
  const fetchInitialDashboardData = async () => {
    if (!user?.id) return;
    if (user?.id) {
      setLoading(true);
      try {
        await dispatch(
          getAuditLogsApi({ id: Number(id), employee_id: user?.id })
        );
      } catch (error) {
        console.error("Error fetching audit logs:", error);
      } finally {
        setLoading(false);
      }
    }
  };
  const refreshData = useCallback(async () => {
    // setIsRefreshing(true);
    fetchInitialDashboardData();
  }, [dispatch, user?.id]);

  useEffect(() => {
    // refreshData();

    PullToRefresh.init({
      mainElement: "#refresh-container",
      onRefresh: () => refreshData(),
      instructionsPullToRefresh: "Pull to refresh",
      instructionsReleaseToRefresh: "Release to refresh",
      getMarkup: () => `
                  <div class="w-full h-16 flex items-center justify-center bg-white shadow-sm">
                      <div class="w-full flex items-center justify-center">
                      <div class="flex items-center justify-center gap-2 text-base text-gray-600">
                          <div class="h-4 w-4 border-2 border-t-transparent border-gray-500 rounded-full animate-spin"></div>
                          <span>Refreshing...</span>
                      </div>
                      </div>
                  </div>
                  `,
      distThreshold: 40,
      distMax: 60,
      distReload: 50,
      shouldPullToRefresh() {
        const container = document.getElementById("refresh-container");
        return container?.scrollTop === 0;
      },
    });
    return () => {
      PullToRefresh.destroyAll();
      dispatch(setShowTopNav(true));
    };
  }, [refreshData]);

  // useEffect(() => {
  //   if (!isRefreshing) return; // When refresh is not active, hide loader
  //   const timeout = setTimeout(() => setIsRefreshing(false), 10); // Reset refresh state after 2 seconds

  //   return () => clearTimeout(timeout);
  // }, [isRefreshing]);

  return (
    <div id="refresh-container" className="h-full overflow-y-auto bg-white">
      <div className="flex flex-col h-screen text-base text-gray-800">
        <div className="flex-1 overflow-y-auto px-4 space-y-5 mt-6">
          {loading ? (
            <div className="flex justify-center items-center py-4">
              <Loader
                size={32}
                color="#2664eb"
                strokeWidth={2.5}
                absoluteStrokeWidth
                className="animate-spin"
              />
            </div>
          ) : (
            Array.isArray(getAuditLogsData) &&
            getAuditLogsData.map(
              (
                data: { action: ListTypes["type"]; [key: string]: any },
                index
              ) => (
                <div
                  key={index}
                  className="flex items-start space-x-3 relative"
                >
                  <div className="flex flex-col items-center">
                    <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center text-blue-600">
                      {ICONS[data.action as ListTypes["type"]]}
                    </div>
                    {index < getAuditLogsData.length - 1 && (
                      <div className="w-px h-10 bg-gray-300 mt-1"></div>
                    )}
                  </div>

                  <div className="flex-1">
                    {data.action === "add" && (
                      <div className="font-semibold text-gray-800">
                     Added{" "}
                        <span className="text-black">
                          {formatTimeCardDate(data?.record_date)}
                        </span>
                      </div>
                    )}
                    {data.action === "delete" && (
                      <div className="font-semibold text-gray-800">
                        Deleted{" "}
                        <span className="text-black">
                          {data.timeRange} ({data.duration})
                        </span>
                      </div>
                    )}
                    {data.action === "update" && (
                      <>
                        <div className="font-semibold text-gray-800">
                          Updated{" "}
                          {displayColumnValue(data?.column_name_updated)}
                        </div>
                        <div className="text-wrap">
                       

                          {data.column_orig_value && (
                            <div className="text-base text-gray-600 text-wrap">
                              orginal value:{" "}
                              <span className="break-words">
                                {data?.column_name_updated ===
                                "os_emp_end_datetime"
                                  ? `${formatTimeCardTime(
                                      data?.column_orig_value
                                    )} ${formatTimeCardDate(
                                      data?.column_orig_value
                                    )}`
                                  : data?.column_orig_value}
                              </span>
                            </div>
                          )}
                             {data?.column_new_value && (
                            <div
                              className="text-base text-gray-600 break-words"
                              style={{ wordBreak: "break-word" }}
                            >
                              updated value:{" "}
                              <span className="break-words">
                                {/* {data?.column_new_value} */}
                                {data?.column_name_updated ===
                                "os_emp_end_datetime"
                                  ? `${formatTimeCardTime(
                                      data?.column_new_value
                                    )} ${formatTimeCardDate(
                                      data?.column_new_value
                                    )}`
                                  : data?.column_new_value}
                              </span>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    {(data.action === "clockin" ||
                      data.type === "clockout") && (
                      <div className="font-semibold text-gray-800">
                        {data.type === "clockin" ? "Clocked in" : "Clocked out"}
                      </div>
                    )}

                    <div className="text-base text-gray-600">
                      {data?.action} by {data?.name} at{" "}
                      {formatTimeCardTime(data?.created)},{" "}
                      {formatTimeCardDate(data?.created)}
                    </div>
                  </div>
                </div>
              )
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangeHistoryPage;
